import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { usePolls } from '@/hooks/usePolls.tsx';
import { useQuizzes } from '@/hooks/useQuizzes';
import { useBrandManager } from '@/hooks/useBrandManager';
import { ListChecks, ClipboardList, Loader2, Plus } from 'lucide-react';

interface InsertPollQuizModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (iframeHtml: string) => void;
}

export const InsertPollQuizModal = ({
  open,
  onOpenChange,
  onInsert,
}: InsertPollQuizModalProps) => {
  const { currentBrandId } = useBrandManager();
  const [activeTab, setActiveTab] = useState<'polls' | 'quizzes'>('polls');

  const { data: polls, isLoading: pollsLoading } = usePolls(currentBrandId || '');
  const { data: quizzes, isLoading: quizzesLoading } = useQuizzes(currentBrandId || '');

  const publishedPolls = polls?.filter((poll) => poll.is_published && poll.share_code) || [];
  const publishedQuizzes = quizzes?.filter((quiz: any) => quiz.is_published && quiz.share_code) || [];

  const handleInsertPoll = (shareCode: string) => {
    const origin = window.location.origin;
    const iframeHtml = `<iframe src="${origin}/poll/${shareCode}" width="100%" height="600" frameborder="0" style="border-radius: 8px; border: none;"></iframe>`;
    onInsert(iframeHtml);
    onOpenChange(false);
  };

  const handleInsertQuiz = (shareCode: string) => {
    const origin = window.location.origin;
    const iframeHtml = `<iframe src="${origin}/quiz/${shareCode}" width="100%" height="600" frameborder="0" style="border-radius: 8px; border: none;"></iframe>`;
    onInsert(iframeHtml);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogTitleWithBug
          popupName="Insert Poll or Quiz"
          popupContext="/articles"
          isOpen={open}
        >
          Insert Poll or Quiz
        </DialogTitleWithBug>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'polls' | 'quizzes')}>
          <TabsList className="w-full">
            <TabsTrigger value="polls" className="flex-1">
              <ClipboardList className="h-4 w-4 mr-2" />
              Polls ({publishedPolls.length})
            </TabsTrigger>
            <TabsTrigger value="quizzes" className="flex-1">
              <ListChecks className="h-4 w-4 mr-2" />
              Quizzes ({publishedQuizzes.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="polls" className="mt-4">
            {pollsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : publishedPolls.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No published polls yet</p>
                <p className="text-sm mt-1">Publish a poll first to embed it here</p>
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {publishedPolls.map((poll) => (
                    <div
                      key={poll.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{poll.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Code: {poll.share_code}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleInsertPoll(poll.share_code!)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Insert
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          <TabsContent value="quizzes" className="mt-4">
            {quizzesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : publishedQuizzes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ListChecks className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No published quizzes yet</p>
                <p className="text-sm mt-1">Publish a quiz first to embed it here</p>
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {publishedQuizzes.map((quiz: any) => (
                    <div
                      key={quiz.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{quiz.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Code: {quiz.share_code}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleInsertQuiz(quiz.share_code!)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Insert
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
